import { supabase, q } from '../lib/supabase.js';
import * as Demo from '../demo/demo.js';

/**
 * Cobrança de documentos.
 *
 * Quem não mandou sai do próprio painel — conta que segue faltante na
 * competência. Não lemos a caixa do Titan: ele é só hospedagem de e-mail,
 * e ler a caixa para marcar recebimento destruiria o controle de a pessoa
 * abrir o arquivo e conferir (decisão da Beatriz).
 *
 * O sistema monta o texto; quem envia é a pessoa, no Titan. Depois registra
 * aqui que saiu — e aí dá para dizer "cobramos três vezes e não veio".
 */

export async function pendentes(competencia) {
  if (Demo.emDemonstracao) return pendentesDemo(competencia);   // guarda do modo demonstração
  return q(supabase.rpc('painel_cobranca', { p_competencia: competencia }));
}

function pendentesDemo(competencia) {
  const mes = competencia.slice(0, 7) + '-01';

  return Demo.empresas
    .filter((e) => e.situacao !== 'encerrado' && !e.matriz_id)
    .map((e) => {
      const contas = e.bancos || [];
      const faltantes = contas.filter((b) => {
        const d = Demo.documentos.find((x) => x.banco_id === b.id && x.competencia === mes);
        return !d || d.status !== 'recebido';
      });
      const doMes = Demo.cobrancas.filter((c) => c.empresa_id === e.id && c.competencia === mes);
      const todas = Demo.cobrancas.filter((c) => c.empresa_id === e.id);
      const ultima = todas.length ? todas.map((c) => c.enviada_em).sort().at(-1) : null;

      return {
        empresa_id: e.id,
        empresa_nome: e.nome,
        codigo: e.codigo,
        email: e.email,
        responsaveis: (e.responsaveis || []).map((r) => r.nome).join(', ') || '—',
        contas_total: contas.length,
        contas_faltantes: faltantes.length,
        contas_nomes: faltantes.map((b) => b.nome).join(', '),
        cobrancas_no_mes: doMes.length,
        ultima_cobranca: ultima,
        dias_sem_cobrar: ultima
          ? Math.round((Date.now() - new Date(ultima)) / 86400000)
          : null,
      };
    })
    .filter((x) => x.contas_faltantes > 0)
    .sort((a, b) => b.contas_faltantes - a.contas_faltantes || a.empresa_nome.localeCompare(b.empresa_nome));
}

export async function registrar({ empresaId, competencia, canal = 'email', faltantes = null, observacao = null }) {
  if (Demo.emDemonstracao) {   // guarda do modo demonstração
    const registro = {
      id: 'cb-demo-' + Date.now(),
      empresa_id: empresaId,
      competencia: competencia.slice(0, 7) + '-01',
      enviada_em: new Date().toISOString(),
      canal, contas_faltantes: faltantes, observacao,
    };
    Demo.cobrancas.push(registro);
    const e = Demo.empresas.find((x) => x.id === empresaId);
    if (e) e.ultima_cobranca = new Date().toISOString().slice(0, 10);
    return registro.id;
  }

  const { data, error } = await supabase.rpc('registrar_cobranca', {
    p_empresa: empresaId,
    p_competencia: competencia,
    p_canal: canal,
    p_faltantes: faltantes,
    p_observacao: observacao,
  });
  if (error) throw new Error(error.message);
  return data;
}

/** Histórico de uma empresa — "já cobramos quantas vezes?" */
export async function historico(empresaId) {
  if (Demo.emDemonstracao) {   // guarda do modo demonstração
    return Demo.cobrancas
      .filter((c) => c.empresa_id === empresaId)
      .sort((a, b) => b.enviada_em.localeCompare(a.enviada_em));
  }

  return q(
    supabase
      .from('cobrancas')
      .select('id, competencia, enviada_em, canal, contas_faltantes, observacao, colaborador:colaboradores ( nome )')
      .eq('empresa_id', empresaId)
      .order('enviada_em', { ascending: false })
  );
}

/* ------------------------------------------------------------------ */
/* Texto da cobrança                                                   */
/*                                                                     */
/* O sistema monta, a pessoa revisa e envia pelo Titan. Não mandamos    */
/* e-mail em nome de ninguém.                                          */
/* ------------------------------------------------------------------ */

const MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

export function montarTexto(linha, competencia) {
  const [ano, mes] = competencia.split('-');
  const refMes = `${MESES[Number(mes) - 1]} de ${ano}`;
  const contas = linha.contas_nomes || 'os extratos bancários';
  const plural = linha.contas_faltantes > 1;

  const insistencia = linha.cobrancas_no_mes > 0
    ? `\n\nEste é o ${linha.cobrancas_no_mes + 1}º contato sobre ${refMes}.`
    : '';

  return `Assunto: Extrato${plural ? 's' : ''} bancário${plural ? 's' : ''} — ${refMes}

Prezados,

Para concluirmos a contabilidade de ${refMes}, ainda precisamos ${plural ? 'dos extratos' : 'do extrato'} em PDF ${plural ? 'das contas' : 'da conta'}: ${contas}.

Lembrando que o PDF é necessário mesmo nas contas integradas por Open Finance.${insistencia}

Assim que recebermos, damos sequência ao fechamento.

Atenciosamente,
Tarter Consultoria Contábil`;
}
